import { api, apiWithRefreshToken } from '.';
import { APIProxy } from './api-proxy';
import { clearAuthCookies } from '../lib/cookies';
import { IFormReview, IFormVehicle } from '../interface/my-account.interface';
import {
  IAllHobbies,
  IAllOccupations,
  IAllProfileDropdown,
  IPayloadSyncSeamlessAccount,
  IResponseCheckSeamlessAccount,
} from '../interface/customer.interfaces';
import { ITrackingDetailPromo } from '../interface/booking-service.interface';

const getMyVehicle = async (accessToken: string, page?: number, size?: number) => {
  const response = await apiWithRefreshToken.get('saphybris-proxy/v1/saphybris/vehicles/list', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
    params: { page, size },
  });

  return response;
};

const getMyVehicleClient = async ({ page, size }: { page?: number; size?: number }) => {
  const response = apiWithRefreshToken.get(`/api/vehicle/get-customer-vehicle`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    params: { page, size },
  });
  return response;
};

const getVehicleMaster = (params: { modelName?: string; modelCode?: string }) => {
  const paramsString = new URLSearchParams([...Object.entries(params)]).toString();
  return api.get(`/saphybris-proxy/v1/saphybris/vehicles/vehicle-master?${paramsString}`);
};

const postVehicle = async (payload: IFormVehicle) => {
  const response = await apiWithRefreshToken.post('api/vehicle/post-vehicle', payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response;
};

const deleteVehicle = async (payload: { licencePlate: string; vin?: string | null }) => {
  const response = await apiWithRefreshToken.post('api/vehicle/delete-vehicle', payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
  return response;
};

const getVehicleByNo = async ({ policeNo }: { policeNo: string }) => {
  const response = await apiWithRefreshToken.get(
    `api/vehicle/get-vehicle-by-no?policeNo=${policeNo}`,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

  return response.data;
};

const getCheckLastService = async (vin: string) => {
  const response = await apiWithRefreshToken.get(`api/vehicle/check-last-service?vin=${vin}`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const postReview = async (payload: IFormReview[]) => {
  const response = await apiWithRefreshToken.post('api/feedback', payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const getTrackingServiceDetail = async ({ orderNo }: { orderNo: string }) => {
  const response = await apiWithRefreshToken.get(
    `api/tracking-service/get-service-detail?orderNo=${orderNo}`,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

  return response.data;
};

const getSelectedPromoTrackingServiceDetail = async ({
  orderNo,
}: {
  orderNo: string;
}): Promise<ITrackingDetailPromo> => {
  const response = await apiWithRefreshToken.get(
    `api/tracking-service/get-selected-promo-service-detail?orderNo=${orderNo}`,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

  return response.data;
};

const rescheduleService = async (payload: any) => {
  const response = await apiWithRefreshToken.post(`api/tracking-service/reschedule`, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const submitFeedbackService = async (payload: any) => {
  const response = await apiWithRefreshToken.post(`api/tracking-service/submit-feedback`, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const getECertificate = async (orderId: string, entryNo: number) => {
  try {
    const response = await api.get(
      `api/download-ecertificate?orderId=${orderId}&entryNo=${entryNo}`,
      {
        baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
      }
    );
    return response.data;
  } catch (err) {
    console.log(err);
  }
};

const getAllOccupations = async (accessToken: string): Promise<{ data: IAllOccupations }> => {
  const response = apiWithRefreshToken.get('saphybris-proxy/v1/saphybris/occupations', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response;
};

const getAllDropdown = async (accessToken: string): Promise<{ data: IAllProfileDropdown }> => {
  const response = apiWithRefreshToken.get('saphybris-proxy/v1/saphybris/profile-master-data', {
    headers: {
      Authorization: `Bearer ${accessToken}`,
    },
  });

  return response;
};

const updateProfilePicture = async (payload: { pictureUrl: string }) => {
  const response = await apiWithRefreshToken.post(
    'api/my-profile/update-profile-picture',
    payload,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

  return response;
};

const updateProfileInformation = async (payload) => {
  const response = await apiWithRefreshToken.patch('api/my-profile/update-profile', payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });
  return response;
};

const getSendEmailVerification = async (email: string) => {
  const response = await apiWithRefreshToken.get(
    `api/my-profile/get-send-email-activation-profile?email=${email}`,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

  return response.data;
};

const getAllHobbies = async (token: string): Promise<{ data: IAllHobbies }> => {
  const response = await apiWithRefreshToken.get('saphybris-proxy/v1/saphybris/hobbies', {
    headers: {
      Authorization: `Bearer ${token}`,
    },
  });
  return response;
};

const getServiceBookingList = async ({
  serviceType,
  orderType,
  accessToken,
}: {
  serviceType: string;
  orderType: string;
  accessToken: string;
}) => {
  const response = await apiWithRefreshToken.get(
    `api/get-listing-service-booking?serviceType=${serviceType}&orderType=${orderType}&accessToken=${accessToken}`,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

  return response.data;
};

const postCheckPhoneNumber = async (payload: { phoneNumber: string }) => {
  const response = await apiWithRefreshToken.post('api/my-profile/check-phone-number', payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response;
};

const postLogout = async () => {
  const response = await apiWithRefreshToken.post(
    'api/my-profile/post-logout',
    {},
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );
  return response;
};

const getSeamlessAccount = async (): Promise<IResponseCheckSeamlessAccount> => {
  const response = await apiWithRefreshToken.get(`api/my-profile/get-seamless-account`, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const postSyncSeamlessAccount = async (payload: IPayloadSyncSeamlessAccount): Promise<any> => {
  const response = await apiWithRefreshToken.post(`api/my-profile/sync-seamless-account`, payload, {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const getQr = async () => {
  const response = await apiWithRefreshToken.get('api/qr', {
    baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
  });

  return response.data;
};

const postCustomerCheckAccount = async (payload: { email?: string; phoneNumber?: string }) => {
  const response = await apiWithRefreshToken.post(
    'api/my-profile/customers-check-account',
    payload,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );

  return response;
};

const sendAccountDeletionRequest = async (otpType: 'WhatsApp' | 'SMS') => {
  const response = await apiWithRefreshToken.post(
    `api/my-profile/send-account-deletion-request?otpType=${otpType}`,
    null,
    {
      baseURL: process.env.NEXT_PUBLIC_BASEPATH_URL,
    }
  );
  return response;
};

const verifyAccountDeletion = async (token: string, req: any, res: any) => {
  const apiProxy = new APIProxy(req, res);
  const response = await apiProxy.makeRequest({
    method: 'GET',
    url: `saphybris-proxy/v1/saphybris/account-deletion?token=${token}&needVerification=true`,
  });
  if (response?.status === 200) {
    clearAuthCookies(res);
  }
  return response;
};

export default {
  getMyVehicle,
  getVehicleMaster,
  postVehicle,
  deleteVehicle,
  getVehicleByNo,
  getCheckLastService,
  postReview,
  getTrackingServiceDetail,
  getSelectedPromoTrackingServiceDetail,
  rescheduleService,
  submitFeedbackService,
  getECertificate,
  getAllOccupations,
  getAllDropdown,
  updateProfilePicture,
  updateProfileInformation,
  getSendEmailVerification,
  getAllHobbies,
  getServiceBookingList,
  postCheckPhoneNumber,
  postLogout,
  getSeamlessAccount,
  postSyncSeamlessAccount,
  getQr,
  postCustomerCheckAccount,
  getMyVehicleClient,
  sendAccountDeletionRequest,
  verifyAccountDeletion,
};

